import type { ReactNode } from 'react';

import {
  CitationRequestReviewList,
  type ReviewListHelpRequestContext,
} from './CitationRequestReviewList';
import type { CitationRequest, ProposedReference } from '../../model/citationRequest';
import type { ExtractedStatement } from '../../model/extractedStatements';

import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';

export interface CitationRequestReviewFooterProps {
  isPublishing: boolean;
  hasCandidates: boolean;
  publishCta: {
    label: string;
    isDisabled: boolean;
    onPress: () => void;
  };
  handleDismiss: () => void;
}

interface CitationRequestReviewBodyProps {
  citationRequest: CitationRequest;
  /** Parsed `extractedStatements` from the parent message, forwarded
   * to the list so each "STATEMENT N" header renders an excerpt. */
  extractedStatements: readonly ExtractedStatement[];
  /** Hook returns from `useCitationRequestReviewSheet` — passed in by
   * the wrapper rather than queried inside the body, so the wrapper
   * can also read `isOpen` to drive its overlay. */
  isPublishing: boolean;
  errorMessage: string | null;
  hasCandidates: boolean;
  isApproved: (reference: ProposedReference) => boolean;
  onToggle: (referenceId: string) => void;
  publishCta: CitationRequestReviewFooterProps['publishCta'];
  handleDismiss: () => void;
  /** Render-prop callback for the footer (Cancel + Publish row).
   * Both the standalone sheet and the inline triage-sheet wizard
   * supply their own footer to control which dismiss action runs and
   * to keep the buttons inside their respective overlay chrome. */
  renderFooter?: (footerProps: CitationRequestReviewFooterProps) => ReactNode;
  /** Optional context for the per-statement help-request rows. When
   * supplied, each `ReviewGroupBody` mounts a
   * `StatementReferenceHelpRequestRow`. Omit to disable help-request
   * rows entirely (e.g. for read-only callers). */
  helpRequestContext?: ReviewListHelpRequestContext;
  /** Optional bottom inset applied to the scroll content container.
   * Set by callers that overlay a pinned footer bar on top of the
   * scroll area (e.g. the wizard's glass action bar) so the last
   * list item can scroll above the bar instead of being hidden. */
  scrollBottomInset?: number;
  /** Optional trailing slot appended after the last statement group
   * inside the scroll viewport. Forwarded to
   * `CitationRequestReviewList`; used by the wizard to render an
   * in-content "Start over" link that scrolls with the references
   * rather than pinning to the bottom. */
  endSlot?: ReactNode;
}

/**
 * Presentational body of the citation-request review surface. Owns
 * the title-less list of proposed-reference candidates, the inline
 * error message, and (optionally, via `renderFooter`) the Cancel +
 * Publish CTA row. Shared by:
 *
 *   - `CitationRequestReviewSheet` / `.web.tsx` — wraps this body in
 *     a standalone Actionsheet / Modal, supplies its own close button
 *     in the header, and renders the footer inline.
 *   - `StatementTriageSheet` review-phase render — wraps this body in
 *     the in-progress wizard's existing Actionsheet / Modal and
 *     supplies the footer with a Cancel-to-close-triage handler.
 *
 * The hook (`useCitationRequestReviewSheet`) stays at the wrapper
 * level so each caller can read `isOpen` for its overlay chrome.
 */
export function CitationRequestReviewBody({
  citationRequest,
  extractedStatements,
  isPublishing,
  errorMessage,
  hasCandidates,
  isApproved,
  onToggle,
  publishCta,
  handleDismiss,
  renderFooter,
  helpRequestContext,
  scrollBottomInset,
  endSlot,
}: CitationRequestReviewBodyProps) {
  return (
    <>
      {hasCandidates ? (
        <CitationRequestReviewList
          citationRequest={citationRequest}
          extractedStatements={extractedStatements}
          isApproved={isApproved}
          onToggle={onToggle}
          isPublishing={isPublishing}
          helpRequestContext={helpRequestContext}
          scrollBottomInset={scrollBottomInset}
          endSlot={endSlot}
        />
      ) : (
        <EmptyState />
      )}
      {errorMessage ? (
        <Text size="sm" className="px-4 pb-1 text-error-600">
          {errorMessage}
        </Text>
      ) : null}
      {renderFooter
        ? renderFooter({
            isPublishing,
            hasCandidates,
            publishCta,
            handleDismiss,
          })
        : null}
    </>
  );
}

function EmptyState() {
  return (
    <VStack className="w-full items-center gap-2 px-4 pb-2 pt-2" space="sm">
      <Text size="sm" className="text-center text-typography-700">
        We couldn't find supporting references for the selected statements. Close this and run a new
        search from the Find references pill on your message.
      </Text>
    </VStack>
  );
}
